import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { GenericPageSkeleton } from '@/components/skeletons/GenericPageSkeleton';
import { ChunkErrorBoundary } from '@/components/ChunkErrorBoundary';
import { ChunkFallback } from '@/components/ui/chunk-fallback';
import { InlineLoader } from '@/components/ui/loading';

// Lazy load components using standard React.lazy
const HiringCenter = React.lazy(() => import("@/pages/HiringCenter"));
const ActiveJobs = React.lazy(() => import("@/pages/hiring/ActiveJobs"));
const PausedJobs = React.lazy(() => import("@/pages/hiring/PausedJobs"));
const PastJobs = React.lazy(() => import("@/pages/hiring/PastJobs"));
const ArchivedJobs = React.lazy(() => import("@/pages/hiring/ArchivedJobs"));
const CandidatesPage = React.lazy(() => import("@/pages/CandidatesPage"));
const JobDetailPage = React.lazy(() => import("@/pages/JobDetailPage"));
const CompanyProfilePage = React.lazy(() => import("@/pages/CompanyProfilePage"));
const TeamManagementPage = React.lazy(() => import("@/pages/TeamManagementPage"));
const JobBoard = React.lazy(() => import("@/pages/workspace/JobBoard"));

// Internal portal - lazy loaded
const InternalDashboard = React.lazy(() => import("@/pages/internal/InternalDashboard"));
const EmailTemplateManagement = React.lazy(() => import("@/pages/internal/EmailTemplateManagement"));
const ManagementHub = React.lazy(() => import("@/pages/internal/ManagementHub"));
const UserManagementPage = React.lazy(() => import("@/pages/workspace/internal/UserManagementPage"));
const PricingManagement = React.lazy(() => import("@/pages/internal/PricingManagement"));
const DemoAnalytics = React.lazy(() => import("@/pages/internal/DemoAnalytics"));

// Application Management removed as per cleanup plan
const UnifiedTicketManagement = React.lazy(() => import('@/pages/internal/UnifiedTicketManagement').then(m => ({ default: m.UnifiedTicketManagement })));
const InternalTeamManagement = React.lazy(() => import('@/pages/internal/InternalTeamManagement'));


// Recruiter routes - NEW STRUCTURE
const RecruiterNetwork = React.lazy(() => import("@/pages/recruiter/RecruiterNetwork"));
const RecruiterEarningsPage = React.lazy(() => import("@/pages/recruiter/RecruiterEarningsPage"));
const RecruiterReferralTokensPage = React.lazy(() => import("@/pages/RecruiterReferralTokensPage"));

// Talent routes - lazy loaded
const TalentJobSearch = React.lazy(() => import("@/pages/talent/TalentJobSearch"));
const TalentApplications = React.lazy(() => import("@/pages/talent/TalentApplications"));
const SavedJobs = React.lazy(() => import("@/pages/talent/SavedJobs"));
const InterviewBooking = React.lazy(() => import("@/pages/talent/InterviewBooking"));

// Chat components - lazy loaded
const ModernChat = React.lazy(() => import("@/pages/ModernChat"));

// Nanny platform routes - lazy loaded
const NannyDashboard = React.lazy(() => import("@/pages/nanny-platform/NannyDashboard"));
const NannyJobs = React.lazy(() => import("@/pages/nanny-platform/NannyJobs"));
const NannyProfile = React.lazy(() => import("@/pages/nanny-platform/NannyProfile"));
const ApplicationsList = React.lazy(() => import("@/pages/nanny-platform/ApplicationsList"));
const NannyCommunity = React.lazy(() => import("@/pages/nanny-platform/NannyCommunity"));
const NannyCommunityMe = React.lazy(() => import("@/pages/nanny-platform/NannyCommunityMe"));

// Mother platform routes - lazy loaded
const MotherJobs = React.lazy(() => import("@/pages/nanny-platform/MotherJobs"));
const CreateJobPosting = React.lazy(() => import("@/pages/nanny-platform/CreateJobPosting"));
const JobDetails = React.lazy(() => import("@/pages/nanny-platform/JobDetails"));
const MotherCommunity = React.lazy(() => import("@/pages/nanny-platform/MotherCommunity"));
const MotherCommunityMe = React.lazy(() => import("@/pages/nanny-platform/MotherCommunityMe"));

// Non-lazy components (lightweight)
import { PlanDetail } from "@/components/hiring/PlanDetail";
import { EnhancedCandidateProfile } from "@/components/hiring/EnhancedCandidateProfile";

// Enhanced Suspense wrapper with error boundary and fallback
function SuspenseWrapper({ children, variant = 'default' }: { children: React.ReactNode; variant?: 'workspace' | 'jobs' | 'candidates' | 'header' | 'default' }) {
  return (
    <ChunkErrorBoundary 
      maxRetries={5}
      fallback={
        <ChunkFallback 
          onReload={() => {
            // Clear cache and reload
            if ('caches' in window) {
              caches.keys().then(names => {
                names.forEach(name => caches.delete(name));
              });
            }
            // Use navigation helper for safer reload
            import('@/hooks/useNavigationHelper').then(async ({ useNavigationHelper }) => {
              const { safeReload } = useNavigationHelper();
              await safeReload('optimized-routes-chunk-error');
            }).catch(() => {
              console.warn('⚠️ OptimizedRoutes fallback to navigation recovery');
              try {
                // Try navigation-based recovery instead of hard reload
                window.history.replaceState(null, '', window.location.pathname);
                window.dispatchEvent(new PopStateEvent('popstate'));
              } catch (navError) {
                console.warn('Navigation recovery failed; please refresh manually if issue persists:', navError);
              }
            });
          }}
        />
      }
    >
      <Suspense fallback={variant === 'header' ? (
        <div className="flex items-center justify-center py-8">
          <InlineLoader size="sm" />
        </div>
      ) : (
        <GenericPageSkeleton contentCards={3} variant="compact" />
      )}>
        {children}
      </Suspense>
    </ChunkErrorBoundary>
  )
}

export const CompanyWorkspaceRoutes = () => (
  <Routes>
    <Route path="company/community" element={<Navigate to="/workspace/company/hiring-center" replace />} />
    <Route path="company/community/company" element={<Navigate to="/workspace/company/hiring-center" replace />} />
    
    <Route path="company/hiring-center" element={
      <SuspenseWrapper variant="workspace">
        <HiringCenter />
      </SuspenseWrapper>
    } />
    <Route path="company/hiring-center/active" element={
      <SuspenseWrapper variant="jobs">
        <ActiveJobs />
      </SuspenseWrapper>
    } />
    <Route path="company/hiring-center/paused" element={
      <SuspenseWrapper variant="jobs">
        <PausedJobs />
      </SuspenseWrapper>
    } />
    <Route path="company/hiring-center/past" element={
      <SuspenseWrapper variant="jobs">
        <PastJobs />
      </SuspenseWrapper>
    } />
    <Route path="company/hiring-center/archived" element={
      <SuspenseWrapper variant="jobs">
        <ArchivedJobs />
      </SuspenseWrapper>
    } />
    <Route path="company/hiring-center/plan/:id" element={<PlanDetail />} />
    <Route path="company/candidates" element={
      <SuspenseWrapper variant="candidates">
        <CandidatesPage />
      </SuspenseWrapper>
    } />
    <Route path="company/candidates/:candidateId/profile" element={<EnhancedCandidateProfile />} />
    
    <Route path="company/company-profile" element={
      <SuspenseWrapper>
        <CompanyProfilePage />
      </SuspenseWrapper>
    } />
    <Route path="company/team-management" element={
      <SuspenseWrapper>
        <TeamManagementPage />
      </SuspenseWrapper>
    } />
    <Route path="company/organizations" element={<Navigate to="/workspace/company/company-profile" replace />} />
    <Route path="company/job-board" element={
      <SuspenseWrapper>
        <JobBoard />
      </SuspenseWrapper>
    } />
    
    <Route path="*" element={<div className="min-h-screen flex items-center justify-center"><div className="text-center"><h1 className="text-4xl font-bold mb-4">404</h1><p className="text-xl text-gray-600 mb-4">Page not found</p></div></div>} />
  </Routes>
);

export const RecruiterWorkspaceRoutes = () => (
  <Routes>
    {/* New recruiter routes */}
    <Route path="recruiter/network" element={
      <SuspenseWrapper>
        <RecruiterNetwork />
      </SuspenseWrapper>
    } />
    <Route path="recruiter/earnings" element={
      <SuspenseWrapper>
        <RecruiterEarningsPage />
      </SuspenseWrapper>
    } />
    <Route path="recruiter/referral-links" element={
      <SuspenseWrapper>
        <RecruiterReferralTokensPage />
      </SuspenseWrapper>
    } />
    
    {/* Legacy route redirects - all redirect to network */}
    <Route path="recruiter/community" element={<Navigate to="/workspace/recruiter/network" replace />} />
    <Route path="recruiter/community/me" element={<Navigate to="/workspace/recruiter/network" replace />} />
    <Route path="recruiter/job-search" element={<Navigate to="/workspace/recruiter/network" replace />} />
    <Route path="recruiter/applications" element={<Navigate to="/workspace/recruiter/network" replace />} />
    <Route path="recruiter/applications/:jobId" element={<Navigate to="/workspace/recruiter/network" replace />} />
    <Route path="recruiter/talent-search" element={<Navigate to="/workspace/recruiter/network" replace />} />
    <Route path="recruiter/placements" element={<Navigate to="/workspace/recruiter/network" replace />} />
    <Route path="recruiter/candidates" element={<Navigate to="/workspace/recruiter/network" replace />} />
    <Route path="recruiter/campaigns" element={<Navigate to="/workspace/recruiter/network" replace />} />
    <Route path="recruiter/calendar" element={<Navigate to="/workspace/recruiter/network" replace />} />
    
    <Route path="*" element={<div className="min-h-screen flex items-center justify-center"><div className="text-center"><h1 className="text-4xl font-bold mb-4">404</h1><p className="text-xl text-gray-600 mb-4">Page not found</p></div></div>} />
  </Routes>
);

export const TalentWorkspaceRoutes = () => (
  <Routes>
    {/* New talent routes */}
    <Route path="talent/jobs" element={
      <SuspenseWrapper variant="jobs">
        <TalentJobSearch />
      </SuspenseWrapper>
    } />
    <Route path="talent/applications" element={
      <SuspenseWrapper>
        <TalentApplications />
      </SuspenseWrapper>
    } />
    <Route path="talent/saved" element={
      <SuspenseWrapper>
        <SavedJobs />
      </SuspenseWrapper>
    } />
    <Route path="talent/interview-booking/:applicationId/:stageId" element={
      <SuspenseWrapper>
        <InterviewBooking />
      </SuspenseWrapper>
    } />
    
    <Route path="talent/*" element={<Navigate to="/workspace/talent/jobs" replace />} />
    
    {/* Legacy personal routes - redirect to talent */}
    <Route path="personal/community" element={<Navigate to="/workspace/talent/jobs" replace />} />
    <Route path="personal/community/me" element={<Navigate to="/workspace/talent/jobs" replace />} />
    <Route path="personal/jobs" element={<Navigate to="/workspace/talent/jobs" replace />} />
    <Route path="personal/applications" element={<Navigate to="/workspace/talent/applications" replace />} />
    <Route path="personal/applications/saved" element={<Navigate to="/workspace/talent/saved" replace />} />
    <Route path="personal/*" element={<Navigate to="/workspace/talent/jobs" replace />} />
    
    <Route path="*" element={<div className="min-h-screen flex items-center justify-center"><div className="text-center"><h1 className="text-4xl font-bold mb-4">404</h1><p className="text-xl text-gray-600 mb-4">Page not found</p></div></div>} />
  </Routes>
);

export const InternalWorkspaceRoutes = () => {
  const navigate = useNavigate();
  
  return (
  <Routes>
    <Route path="internal/dashboard" element={
      <SuspenseWrapper variant="workspace">
        <InternalDashboard />
      </SuspenseWrapper>
    } />
    <Route path="internal/management" element={
      <SuspenseWrapper>
        <ManagementHub />
      </SuspenseWrapper>
    } />
    <Route path="internal/user-management" element={
      <SuspenseWrapper variant="workspace">
        <UserManagementPage />
      </SuspenseWrapper>
    } />
    {/* Application Management route removed as per cleanup plan */}
    <Route path="internal/tickets" element={
      <SuspenseWrapper>
        <UnifiedTicketManagement />
      </SuspenseWrapper>
    } />
    <Route path="internal/team" element={
      <SuspenseWrapper>
        <InternalTeamManagement />
      </SuspenseWrapper>
    } />
    <Route path="internal/pricing" element={
      <SuspenseWrapper>
        <PricingManagement />
      </SuspenseWrapper>
    } />
    
    <Route path="internal/email-templates" element={
      <SuspenseWrapper>
        <EmailTemplateManagement />
      </SuspenseWrapper>
    } />
    
    <Route path="internal/demo-analytics" element={
      <SuspenseWrapper>
        <DemoAnalytics />
      </SuspenseWrapper>
    } />
    
    {/* Legacy route redirects */}
    <Route path="internal/recruiter-requests" element={<Navigate to="/workspace/internal/tickets?category=recruiter_application" replace />} />
    
    {/* Catch-all for internal routes - only redirect if truly invalid */}
    <Route path="internal/*" element={
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold mb-4">404</h1>
          <p className="text-xl text-muted-foreground mb-4">Internal page not found</p>
          <button 
            onClick={() => navigate('/workspace/internal/dashboard')}
            className="px-4 py-2 bg-primary text-primary-foreground rounded hover:bg-primary/90"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    } />
    <Route index element={<Navigate to="internal/dashboard" replace />} />
  </Routes>
  );
};

export const NannyWorkspaceRoutes = () => (
  <Routes>
    <Route path="nanny/dashboard" element={<SuspenseWrapper variant="workspace"><NannyDashboard /></SuspenseWrapper>} />
    <Route path="nanny/jobs" element={<SuspenseWrapper><NannyJobs /></SuspenseWrapper>} />
    <Route path="nanny/jobs/:jobId" element={<SuspenseWrapper><JobDetails /></SuspenseWrapper>} />
    <Route path="nanny/profile" element={<SuspenseWrapper><NannyProfile /></SuspenseWrapper>} />
    <Route path="nanny/applications" element={<SuspenseWrapper><ApplicationsList /></SuspenseWrapper>} />
    <Route path="nanny/community" element={<SuspenseWrapper><NannyCommunity /></SuspenseWrapper>} />
    <Route path="nanny/community/me" element={<SuspenseWrapper><NannyCommunityMe /></SuspenseWrapper>} />
    <Route path="nanny/*" element={<Navigate to="/workspace/nanny/dashboard" replace />} />
    <Route path="*" element={<div className="min-h-screen flex items-center justify-center"><div className="text-center"><h1 className="text-4xl font-bold mb-4">404</h1><p className="text-xl text-muted-foreground mb-4">Page not found</p></div></div>} />
  </Routes>
);

export const MotherWorkspaceRoutes = () => (
  <Routes>
    <Route path="mother/jobs" element={<SuspenseWrapper><MotherJobs /></SuspenseWrapper>} />
    <Route path="mother/jobs/create" element={<SuspenseWrapper><CreateJobPosting /></SuspenseWrapper>} />
    <Route path="mother/jobs/:jobId" element={<SuspenseWrapper><JobDetails /></SuspenseWrapper>} />
    <Route path="mother/community" element={<SuspenseWrapper><MotherCommunity /></SuspenseWrapper>} />
    <Route path="mother/community/me" element={<SuspenseWrapper><MotherCommunityMe /></SuspenseWrapper>} />
    <Route path="mother/*" element={<Navigate to="/workspace/mother/jobs" replace />} />
    <Route path="*" element={<div className="min-h-screen flex items-center justify-center"><div className="text-center"><h1 className="text-4xl font-bold mb-4">404</h1><p className="text-xl text-muted-foreground mb-4">Page not found</p></div></div>} />
  </Routes>
);